import React, { useCallback, useEffect, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import {
  compactLayout,
  getCompactAssistanceBarColor,
  isSinAsistenciaStatus,
  mergePositions,
  transformScheduleToLayout,
} from "../../../utils/index.ts";
import GridLayout from "react-grid-layout";
import {
  generateGradientBackground,
  getBackgroundColor,
} from "../../../utils/constanst.js";
import moment from "moment";
import { findByExactDateAndBranch } from "../../../services/branchSchedule.service.ts";
import { findUnscheduleChecks } from "../../../services/assistanceCheck.service.ts";
import Loader from "../../Loader.tsx";
import { findConfigurationByKey } from "../../../services/configuration.service.ts";
import { isScheduleItemCoveringEssentialSlot } from "./config-horario-semanal/scheduleKeyUtils.ts";

const ScheduleCompactDate = ({
  hours,
  formik,
  positionsArray,
  selectedDate,
  turno = 0,
  hoursRange,
  compactFilters,
}) => {
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [unscheduledChecks, setUnscheduleChecks] = useState([]);
  const [colors, setColors] = useState();
  const [configuration, setConfiguration] = useState();
  const [pressedItem, setPressedItem] = useState(null);

  const fetchDependencies = useCallback(async () => {
    if (!selectedDate || !formik.values?.branchId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const dateStr = moment(selectedDate).format("YYYY-MM-DD");

      // Obtener configuración de colores y asistencia
      const colorConfig = await findConfigurationByKey("scheduleColor");
      setColors(colorConfig);
      const assistanceConfig = await findConfigurationByKey("assistance");
      setConfiguration(assistanceConfig);

      // Obtener horarios programados del día
      const fetchedSchedules = await findByExactDateAndBranch(
        dateStr,
        Number(formik.values?.branchId),
      );

      // Transformar horarios a layout con colores según estado
      const transformLayout = transformScheduleToLayout(
        fetchedSchedules,
        colorConfig,
        assistanceConfig,
        { defaultExactDate: dateStr },
      );
      setSchedules(transformLayout);

      // Obtener checks sin horario programado
      const unscheduled = await findUnscheduleChecks(
        Number(formik.values?.branchId),
        dateStr,
        dateStr,
      );
      // La respuesta es un array de objetos con estructura { date, records: { records, partners } }
      setUnscheduleChecks(unscheduled || []);
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, formik.values?.branchId]);

  useEffect(() => {
    fetchDependencies();
  }, [fetchDependencies]);

  // Filtrar posiciones para mostrar solo las que tienen horarios ese día
  const getPositionsWithSchedules = () => {
    if (!schedules.length && !unscheduledChecks.length) {
      return positionsArray;
    }

    // Obtener posiciones únicas de los horarios programados
    const scheduledPositionIds = new Set<number>();
    const scheduledPositionNames = new Set<string>();

    schedules.forEach((schedule: any) => {
      // Agregar por ID de posición
      if (schedule.positionOriginalId_) {
        scheduledPositionIds.add(schedule.positionOriginalId_);
      }
      if (schedule.positionId) {
        scheduledPositionIds.add(schedule.positionId);
      }
      // Agregar por nombre de posición
      if (schedule.position) {
        scheduledPositionNames.add(schedule.position);
      }
      if (schedule.positionName) {
        scheduledPositionNames.add(schedule.positionName);
      }
    });

    // Obtener posiciones únicas de los checks sin horario
    unscheduledChecks.forEach((unscheduleItem) => {
      if (unscheduleItem?.records?.partners) {
        unscheduleItem.records.partners.forEach((partner: any) => {
          if (partner?.candidate?.vacancy?.position?.id) {
            scheduledPositionIds.add(partner.candidate.vacancy.position.id);
          }
          if (partner?.candidate?.vacancy?.position?.name) {
            scheduledPositionNames.add(partner.candidate.vacancy.position.name);
          }
        });
      }
    });

    // Filtrar posiciones que coincidan con las que tienen horarios
    const filteredPositions = positionsArray.filter((position: any) => {
      // Si es un título (Matutino, Vespertino, etc.), mantenerlo
      if (position.title) {
        return true;
      }

      // Verificar si la posición tiene horarios asignados
      const hasScheduleById =
        scheduledPositionIds.has(position.positionOriginalId_) ||
        scheduledPositionIds.has(position.positionId);
      const hasScheduleByName =
        scheduledPositionNames.has(position.positionName) ||
        scheduledPositionNames.has(position.originalPositionName);

      return hasScheduleById || hasScheduleByName;
    });

    // Reconstruir la estructura con títulos si es necesario
    if (formik.values.shift === 3) {
      const shiftOrder = ["Matutino", "Intermedio", "Vespertino"];
      const resultArray: any[] = [];
      let currentShift = "";

      filteredPositions.forEach((position) => {
        if (position.shift !== currentShift) {
          currentShift = position.shift;
          // Verificar si hay posiciones de este turno
          const hasPositionsInShift = filteredPositions.some(
            (p) => p.shift === currentShift && !p.title,
          );
          if (hasPositionsInShift) {
            resultArray.push({ title: currentShift, shift: currentShift });
          }
        }
        resultArray.push(position);
      });

      return resultArray;
    }

    return filteredPositions;
  };

  const filteredPositions = getPositionsWithSchedules();

  // Crear layout para checks sin horario programado en el grid
  const createUnscheduleLayout = () => {
    if (!unscheduledChecks.length || !hoursRange.length) return [];

    const layoutItems: any[] = [];
    const maxY = mergePositions(filteredPositions).length;
    let rowIndex = 0;

    // Procesar cada fecha con checks sin horario
    unscheduledChecks.forEach((unscheduleItem) => {
      if (!unscheduleItem?.records) return;

      if (unscheduleItem.records.records && unscheduleItem.records.partners) {
        const { records, partners } = unscheduleItem.records;

        // Agrupar checks por partner
        partners.forEach((partner) => {
          if (!partner?.candidate) return;

          const partnerChecks = records.filter(
            (r) => r.partner?.id === partner.id,
          );
          if (partnerChecks.length === 0) return;

          // Ordenar checks por hora
          const sortedChecks = partnerChecks.sort((a, b) => {
            const timeA = moment(a.checkTime, "HH:mm:ss");
            const timeB = moment(b.checkTime, "HH:mm:ss");
            return timeA.diff(timeB);
          });

          const firstCheck = sortedChecks[0];
          const lastCheck =
            sortedChecks.length > 1 &&
            sortedChecks[sortedChecks.length - 1].id !== firstCheck.id
              ? sortedChecks[sortedChecks.length - 1]
              : null;

          const checkTime = moment(firstCheck.checkTime, "HH:mm:ss");
          const hourStr = checkTime.format("HH:mm");

          // Encontrar posición X basado en la hora del check
          let xIndex = hoursRange.findIndex((hour, idx) => {
            if (hour.display === "Hora" || idx < 2) return false;
            const [start, end] = hour.display.split(" - ");
            return hourStr >= start && hourStr < end;
          });

          // Si no encuentra posición exacta, buscar la más cercana
          if (xIndex === -1) {
            let closestIndex = -1;
            let minDiff = Infinity;
            const checkMoment = moment(hourStr, "HH:mm");

            hoursRange.forEach((hour, idx) => {
              if (hour.display === "Hora" || idx < 2) return;
              const [start] = hour.display.split(" - ");
              const startMoment = moment(start, "HH:mm");
              const diff = Math.abs(checkMoment.diff(startMoment, "minutes"));

              if (diff < minDiff) {
                minDiff = diff;
                closestIndex = idx;
              }
            });

            if (closestIndex >= 2) {
              xIndex = closestIndex;
            } else {
              return; // No se puede colocar
            }
          }

          const finalXIndex = xIndex >= 2 ? xIndex - 2 : 0;
          if (finalXIndex < 0) return;

          // Ancho fijo 8 hrs (16 slots), sin usar salida
          const width = Math.min(
            16,
            Math.max(2, hoursRange.length - finalXIndex - 2),
          );

          const partnerName = partner.person?.firstName
            ? `${partner.person.firstName} ${partner.person.lastName || ""} ${partner.person.secondLastName || ""}`.trim()
            : "Sin nombre";

          layoutItems.push({
            i: `unscheduled-${partner.id}-${firstCheck.id}`,
            minH: 1,
            maxH: 1,
            minW: 2,
            show: true,
            pending: false,
            name: `${partnerName} (NO PROGRAMADO)`,
            position:
              partner.candidate?.vacancy?.position?.name || "Sin posición",
            originalId: partner.id,
            partnerId: partner.id,
            shift: partner.candidate?.shift?.name || "Sin turno",
            x: finalXIndex,
            y: maxY + rowIndex, // Colocar al final, después de todas las posiciones
            w: width,
            h: 1,
            positionId: -1,
            positionOriginalId_: -1,
            assistanceStatus: "Sin horario programado",
            color: "#9CA3AF", // Gris para no programados
            startTime: hourStr,
            isUnschedule: true,
            entradaTime: hourStr,
            salidaTime: lastCheck
              ? moment(lastCheck.checkTime, "HH:mm:ss").format("HH:mm")
              : null,
          });

          rowIndex++;
        });
      }
    });

    return layoutItems;
  };

  const unscheduleLayout = createUnscheduleLayout();
  const mergedFiltered = mergePositions(filteredPositions);
  const compactedSchedules = compactLayout(schedules, mergedFiltered);
  const headerOffset = unscheduleLayout.length > 0 ? 1 : 0;
  const baseYOffset = mergedFiltered.length + headerOffset;
  const finalLayout = [
    ...compactedSchedules.map((item) => ({ ...item })),
    ...unscheduleLayout.map((item, idx) => ({
      ...item,
      y: baseYOffset + idx,
    })),
  ];

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "visible",
        position: "relative",
      }}
    >
      <Loader isLoading={loading} />
      <div
        style={{
          position: "sticky",
          top: -10,
          zIndex: 5,
          backgroundColor: "white",
          marginBottom: "10px",
        }}
      >
        <GridLayout
          className="layout"
          layout={Array.from({ length: hoursRange.length }, (_, i) => ({
            i: i.toString(),
            x: i,
            y: 0,
            w: 1,
            h: 1,
            static: true,
          }))}
          cols={hoursRange.length}
          rowHeight={40}
          width={2400}
          margin={[0, 0]}
          compactType={null}
        >
          {hoursRange.map((hour, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                textAlign: "center",
                borderRight: index !== 0 ? "2px solid #EEE" : "none",
                borderBottom: "2px solid #EEE",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: 600,
                fontSize: "10px",
                backgroundColor: "white",
              }}
            >
              {index > 1 && hour.display}
            </div>
          ))}
        </GridLayout>
      </div>
      <div className="d-flex flex-row" style={{ minWidth: "2400px" }}>
        <div
          style={{
            position: "sticky",
            left: 0,
            zIndex: 6,
            backgroundColor: "white",
            flexShrink: 0,
            minWidth: hoursRange?.length ? (2400 / hoursRange.length) * 2 : 200,
          }}
        >
          {mergePositions(filteredPositions)
            .filter((position) => !position.isDeleted)
            .map((key, index) => {
              const isEssentialPositionCovered = (position) => {
                if (!position?.positionIsEssencial || position?.title)
                  return false;
                if (
                  !formik.values.schedule ||
                  formik.values.schedule.length === 0
                )
                  return false;

                return formik.values.schedule.some((scheduleItem) =>
                  isScheduleItemCoveringEssentialSlot(scheduleItem, position),
                );
              };

              return (
                <div
                  key={index}
                  data-grid={{
                    x: 0,
                    y: index,
                    w: key?.title ? 36 : 2,
                    h: 1,
                    static: true,
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    fontSize: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "left",
                    borderRight: "2px solid #EEE",
                    borderBottom: `${key?.title ? "3px" : "2px"} solid #EEE`,
                    borderTop: `${key?.title ? "3px solid" : ""} ${
                      key?.title === "Matutino"
                        ? "#FFD700"
                        : key.title === "Vespertino"
                          ? "#FF6347"
                          : "#4682B4"
                    }`,
                    paddingInline: "5px",
                    fontWeight: 600,
                    height: 50,
                    borderLeft: `3px solid ${
                      !key?.shifts
                        ? key.shift === "Matutino"
                          ? "#FFD700"
                          : key.shift === "Vespertino"
                            ? "#FF6347"
                            : "#4682B4"
                        : "#4682B4"
                    }`,
                    backgroundColor: isEssentialPositionCovered(key)
                      ? "#28a745"
                      : key?.positionIsEssencial && !key?.title
                        ? "#FFF9E6"
                        : "transparent",
                    width: (2400 / hoursRange?.length) * 2,
                  }}
                >
                  {key.title && (
                    <p className="w-100 mt-3 fs-15px fw-800 bg">
                      {key?.title ?? key.positionName}
                    </p>
                  )}
                  <Tooltip title={key?.shifts?.join(", ") ?? ""}>
                    <div>
                      <p
                        className="w-100 mt-auto"
                        style={{
                          fontWeight:
                            isEssentialPositionCovered(key) ||
                            (key?.positionIsEssencial && !key?.title)
                              ? "bold"
                              : "normal",
                          color: isEssentialPositionCovered(key)
                            ? "#ffffff"
                            : "inherit",
                        }}
                      >
                        {key?.positionName}
                      </p>
                    </div>
                  </Tooltip>
                </div>
              );
            })}

          {/* Fila para checks sin horario programado en el sidebar */}
          {unscheduleLayout.length > 0 && (
            <div
              data-grid={{
                x: 0,
                y: mergedFiltered.length,
                w: 36,
                h: 1,
                static: true,
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "10px",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                borderRight: "2px solid #EEE",
                borderBottom: "1px solid #EEE",
                borderTop: "2px solid #9CA3AF",
                paddingInline: "6px",
                fontWeight: 700,
                height: 50,
                borderLeft: "3px solid #9CA3AF",
                backgroundColor: "#F3F4F6",
                width: Math.min(
                  130,
                  hoursRange?.length ? (2400 / hoursRange.length) * 2 : 100,
                ),
              }}
            >
              <p
                className="w-100"
                style={{ whiteSpace: "nowrap", marginBottom: 0 }}
              >
                No programado
              </p>
            </div>
          )}
          {unscheduleLayout.length > 0 &&
            unscheduleLayout.map((item, idx) => (
              <div
                key={`unsched-sidebar-${idx}`}
                data-grid={{
                  x: 0,
                  y: mergedFiltered.length + 1 + idx,
                  w: 36,
                  h: 1,
                  static: true,
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  fontSize: "10px",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  borderRight: "2px solid #EEE",
                  borderBottom: "2px solid #EEE",
                  paddingInline: "6px",
                  fontWeight: 600,
                  height: 50,
                  borderLeft: "3px solid #9CA3AF",
                  backgroundColor: "#F9FAFB",
                  width: Math.min(
                    130,
                    hoursRange?.length ? (2400 / hoursRange.length) * 2 : 100,
                  ),
                }}
              >
                <p
                  className="w-100"
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: 0,
                  }}
                >
                  {item.name}
                </p>
              </div>
            ))}
        </div>

        <div
          style={{
            position: "relative",
            flexShrink: 0,
          }}
        >
          <GridLayout
            cols={hoursRange.length}
            layout={finalLayout}
            maxRows={
              mergedFiltered.length +
              (unscheduleLayout.length > 0 ? unscheduleLayout.length + 1 : 0)
            }
            rowHeight={50}
            width={2400}
            preventCollision={false}
            allowOverlap={true}
            isResizable={false}
            isDroppable={false}
            isDraggable={false}
            draggable={false}
            compactType={null}
            margin={[0, 0]}
            style={{
              background: generateGradientBackground(hoursRange, hours, turno),
              width: `${2400}px`,
              position: "relative",
            }}
          >
            {mergePositions(filteredPositions)
              .filter((position) => !position.isDeleted)
              .map((key, index) => {
                return (
                  <div
                    key={index}
                    data-grid={{
                      x: 0,
                      y: index,
                      w: key?.title ? 36 : 2,
                      h: 1,
                      static: true,
                    }}
                    style={{
                      fontSize: "10px",
                      display: "none",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "left",
                      borderRight: "2px solid #EEE",
                      borderBottom: `${key?.title ? "3px" : "2px"} solid #EEE`,
                      borderTop: `${key?.title ? "3px solid" : ""} ${
                        key?.title === "Matutino"
                          ? "#FFD700"
                          : key.title === "Vespertino"
                            ? "#FF6347"
                            : "#4682B4"
                      }`,
                      paddingInline: "5px",
                      fontWeight: 600,
                      borderLeft: `3px solid ${
                        key.shift === "Matutino"
                          ? "#FFD700"
                          : key.shift === "Vespertino"
                            ? "#FF6347"
                            : "#4682B4"
                      }`,
                      backgroundColor: "white",
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                    }}
                  >
                    {key.title && (
                      <p className="w-100 mt-3 fs-15px fw-800 bg">
                        {key?.title ?? key.positionName}
                      </p>
                    )}
                    <p className="w-100 mt-auto">{key.positionName}</p>
                  </div>
                );
              })}

            {finalLayout.map((item, index) => {
              if (item?.x === undefined || !item?.show) return <></>;

              // Aplicar filtros
              if (
                compactFilters.assistence &&
                item.assistanceStatus !== "Asistencia" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.late &&
                item.assistanceStatus !== "Retardo" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.withoutAssistence &&
                !isSinAsistenciaStatus(item.assistanceStatus) &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (
                compactFilters.fueraDeHorario &&
                item.assistanceStatus !== "Fuera de horario" &&
                !item.isUnschedule
              ) {
                return <></>;
              }
              if (compactFilters.sinHorario && !item.isUnschedule) {
                return <></>; // Si el filtro "sin horario" está activo, ocultar los programados
              }
              if (
                !compactFilters.sinHorario &&
                !compactFilters.resumen &&
                item.isUnschedule
              ) {
                return <></>; // Si no está activo el filtro "sin horario" o "resumen", ocultar los sin horario
              }

              return (
                <div
                  key={item?.i}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    opacity: pressedItem === item.i ? 0.1 : 1,
                  }}
                  data-grid={{
                    x: item?.x,
                    y: item?.y,
                    w: item?.w,
                    h: 1,
                  }}
                >
                  <div
                    style={{
                      paddingLeft: "8px",
                      width: "100%",
                      borderRadius: "5px",
                      paddingBlock: "4px",
                      // En resumen: mostrar colores según estado de asistencia
                      // En filtros específicos: también mostrar colores según estado
                      backgroundColor: item.color || "#9E9E9E",
                      border: `1px solid ${item.color || "#9E9E9E"}80`,
                      borderLeft: `3px solid ${item.color || "#9E9E9E"}`,
                      ...(compactFilters.resumen && {
                        backgroundColor: item.color || "#9E9E9E",
                        border: `1px solid ${item.color || "#9E9E9E"}80`,
                        borderLeft: `3px solid ${item.color || "#9E9E9E"}`,
                      }),
                      ...(compactFilters.assistence && {
                        backgroundColor: item.color,
                      }),
                      ...(compactFilters.late && {
                        backgroundColor: item.color,
                      }),
                      ...(compactFilters.withoutAssistence && {
                        backgroundColor: item.color,
                      }),
                      ...(compactFilters.fueraDeHorario && {
                        backgroundColor: item.color,
                      }),
                      ...(compactFilters.sinHorario && {
                        backgroundColor: item.color,
                      }),
                    }}
                    className="shadow-sm"
                  >
                    <Tooltip
                      title={
                        item.isUnschedule
                          ? `${item.name} | ${item.position}`
                          : `${item.name} | ${item.position} | ${
                              item.w / 2
                            } hrs ${
                              item.isUnschedule
                                ? `| Sin horario programado | Entrada: ${item.entradaTime}${
                                    item.salidaTime
                                      ? ` | Salida: ${item.salidaTime}`
                                      : ""
                                  }`
                                : ""
                            }`
                      }
                      arrow
                    >
                      <div className="d-flex flex-row align-items-center">
                        <Typography fontWeight={500}>
                          <div>{item.name}</div>
                        </Typography>
                        {item.overlap && (
                          <a
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              setPressedItem(item.i);
                            }}
                            onMouseUp={(e) => {
                              e.stopPropagation();
                              setPressedItem(null);
                            }}
                            onMouseLeave={() => setPressedItem(null)}
                            style={{ zIndex: 2 }}
                          >
                            <i className="ms-2 fa-solid fa-eye"></i>
                          </a>
                        )}
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </GridLayout>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCompactDate;
